import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { downloadDocument, searchDocuments } from '@/api/documents';
import { replaceDocumentWithDocx } from '@/lib/officeDoc';
import { Button } from '@/components/Button';

export function DocumentsPane() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [openingId, setOpeningId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['documents', query],
    queryFn: () => searchDocuments(query),
  });

  const onOpen = async (id: string) => {
    try {
      setOpeningId(id);
      const buf = await downloadDocument(id);
      await replaceDocumentWithDocx(buf);
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <div className="flex h-full flex-col gap-3 p-4">
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('documents.search')}
        className="w-full rounded-md border border-lex-slate-200 px-3 py-2 text-sm focus:border-lex-magenta focus:outline-none"
      />
      <div className="flex-1 space-y-2 overflow-y-auto">
        {isLoading ? (
          <p className="text-xs text-lex-slate-500">{t('common.loading')}</p>
        ) : !data?.length ? (
          <p className="text-xs text-lex-slate-500">{t('documents.empty')}</p>
        ) : (
          data.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center justify-between gap-2 rounded-md border border-lex-slate-200 p-2"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{doc.name}</p>
                {doc.caseName ? (
                  <p className="truncate text-xs text-lex-slate-500">
                    {doc.caseName}
                  </p>
                ) : null}
              </div>
              <Button
                size="sm"
                variant="secondary"
                disabled={openingId === doc.id}
                onClick={() => onOpen(doc.id)}
              >
                {openingId === doc.id ? t('common.loading') : t('documents.open')}
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
